import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { KeyboardArrowDown as ArrowDownIcon } from '@material-ui/icons';
import clsx from 'clsx';

import styles from './DropdownPill.module.scss';

const DropdownPill = ({ label, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const pillRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = () => {
    if (!pillRef.current) return;
    const rect = pillRef.current.getBoundingClientRect();
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 6,
      left: rect.left,
      zIndex: 9999,
      minWidth: rect.width,
    });
  };

  const handleToggle = () => {
    if (!open) updateMenuPosition();
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (
        pillRef.current && !pillRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    const handleScroll = () => updateMenuPosition();

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  const activeOption = options.find((o) => o.value === value);
  const displayLabel = activeOption && activeOption.value !== '' ? activeOption.label : label;
  const isActive = value !== '';

  return (
    <div className={styles.wrapper} ref={pillRef}>
      <button
        type="button"
        className={clsx(styles.pill, { [styles.pillActive]: isActive })}
        onClick={handleToggle}
        aria-expanded={open}
      >
        <span className={clsx(styles.label, { [styles.labelActive]: isActive })}>
          {displayLabel}
        </span>
        <ArrowDownIcon className={clsx(styles.arrow, { [styles.arrowActive]: isActive })} />
      </button>

      {open && ReactDOM.createPortal(
        <div className={styles.menu} style={menuStyle} ref={menuRef}>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={clsx(styles.item, { [styles.itemSelected]: opt.value === value })}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

DropdownPill.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default DropdownPill;
